import { FCC } from "@/types/common";
import { Stack, Box, Modal, Typography, styled, Grid } from "@mui/material";
import { IconClose, IconEditor } from "@/assets/icons";
import { IconBox } from "@/components/elements/IconBox";
import { background, black, divider } from "@/theme/colors";
import { Button } from "@/components/elements/Button";
import { CaseMatchDetailInfo } from "@/features/case/components/CaseEdit/CaseMatchDetailInfo";
import { TCaseMatches } from "@/api/services/main/case/dtos/get-case.dto";
import { convertResToFormData } from "@/features/talent/utils/convertTalentFormData";
import { useTalentInfo } from "@/api/services/main/talent";
import { LoadingArea } from "@/components/elements/Loading/Loading";

type TCaseMatchDetailModalProps = {
  isOpen: boolean;
  caseMatch?: TCaseMatches;
  onClose: () => void;
  onEdit: () => void;
  canEdit?: boolean;
};

export const CaseMatchDetailModal: FCC<TCaseMatchDetailModalProps> = ({ isOpen, caseMatch, onClose, onEdit, canEdit = true }) => {
  const talentId = caseMatch?.talent?.id;
  const { data: talentData, isLoading: isTalentLoading } = useTalentInfo(talentId);
  const talentFormData = talentData?.data ? convertResToFormData(talentData.data) : undefined;

  const isLoading = isOpen && caseMatch && talentId && (isTalentLoading || !talentFormData);

  return (
    <Modal
      open={isOpen}
      hideBackdrop={false}
      onClose={(_, reason) => {
        if (reason === "backdropClick") return;
        onClose();
      }}
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: 0,
        "& .MuiBackdrop-root": {
          padding: 0,
        },
      }}
    >
      <ModalContainer>
        <Stack
          direction="row"
          bgcolor={background.disable}
          height="56px"
          minHeight="56px"
          alignItems="center"
          justifyContent="space-between"
          sx={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px", px: 3 }}
        >
          <Typography variant="body14Light" color={"#0B0C0D"}>
            マッチング情報
          </Typography>
          <IconBox onClick={onClose} sx={{ p: 0 }}>
            <IconClose color={black[80]} fontSize={24} />
          </IconBox>
        </Stack>

        <Box sx={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", position: "relative" }}>
          {isLoading ? (
            <LoadingArea />
          ) : (
            <Stack sx={{ flex: 1, overflowY: "auto", px: 4, pb: 4, pt: 1 }}>
              {caseMatch ? (
                <Grid container spacing={4}>
                  <Grid item>
                    <CaseMatchDetailInfo caseMatch={caseMatch} talent={talentFormData} />
                  </Grid>
                </Grid>
              ) : null}
            </Stack>
          )}
        </Box>

        <Box>
          <hr color={divider.high} />
          <Stack direction="row" gap="16px" justifyContent="space-between" p="16px 32px">
            <Button variant="ghost" size="sm" onClick={onClose} sx={{ width: "102px" }}>
              キャンセル
            </Button>
            <Button variant="outline" size="sm" onClick={onEdit} startIcon={<IconEditor />} disabled={!canEdit}>
              編集
            </Button>
          </Stack>
        </Box>
      </ModalContainer>
    </Modal>
  );
};

const ModalContainer = styled(Box)`
  width: 764px;
  height: 728px;
  background-color: ${background.white};
  box-shadow: 0px 8px 24px 0px #00000026;
  border-radius: 8px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  outline: none;
  position: relative;
  margin: auto;
  &:focus {
    outline: none;
  }
`;
